import { Filter, XCircle } from 'react-feather';
import { ReactNode } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { GetEventPickerEventsQuery as GetEventPickerEvents } from '__generated__/graphql';

import { CreateEventCard } from '../Events/EventsPage';
import { useFilterModal } from './FilterModal';
import EventPicker, { useEvents } from '../Events/EventPicker';
import UI from '../../common/UI';
import useDashboardUrlState from './useDashboardUrlState';
import useProject from '../useProject';

type Event = GetEventPickerEvents['project']['events']['data'][0];

export interface DashboardLayoutProps {
  eventId?: string;
  tab?: string;
  children: ReactNode;
}

const DashboardLayout = ({ eventId, tab, children }: DashboardLayoutProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const project = useProject();

  const { called, total } = useEvents();
  const hasNoEvents = called && total === 0;

  const [urlState, setUrlState] = useDashboardUrlState();

  const handleEventChange = (event: Event | null) => {
    if (event) {
      history.push(`/${project.id}/dashboard/${event.id}${tab ? `/${tab}` : ''}`);
    } else {
      history.push(`/${project.id}/dashboard`);
    }
  };

  /**
   * A modal that allows the user to select ticket filters that apply to the entire dashboard.
   */
  const [openFilterModal] = useFilterModal({ eventId });

  /**
   * Removes ticket filters from the URL.
   */
  const resetFilters = () => {
    setUrlState((urlState) => ({ ...urlState, tickets: [] }));
  };

  if (!called) {
    return <UI.PageLoader />;
  }

  if (hasNoEvents) {
    return (
      <UI.Section sc={{ compact: true, mt: [0, 8] }}>
        <CreateEventCard />
      </UI.Section>
    );
  }

  return (
    <UI.Section>
      <UI.GridContainer>
        <UI.FlexContainer sc={{ alignItems: 'center', wrap: 'wrap' }}>
          <UI.Div sc={{ width: ['100%', '300px'] }}>
            <EventPicker
              eventId={eventId}
              onChange={handleEventChange}
              renderActive={(event) => (
                <UI.Span style={{ fontWeight: '500' }}>
                  {event && (
                    <UI.StatusIndicator
                      sc={{ background: event?.active ? 'success' : 'gray.300', mr: 1 }}
                    />
                  )}
                  {event ? event.title : t('all_events')}
                </UI.Span>
              )}
              nullable={total > 1}
            />
          </UI.Div>

          {eventId && (
            <>
              <UI.Button
                onClick={() => openFilterModal()}
                sc={{ brand: urlState.tickets.length > 0 ? 'secondary' : undefined, ml: [0, 2], mt: [3, 0] }}
              >
                <UI.Icon>
                  <Filter />
                </UI.Icon>
                {' '}
                {t('filters')}
                {' '}
                {urlState.tickets.length > 0 && (
                  <UI.Badge sc={{ brand: 'secondary.400' }} style={{ marginTop: -1, marginBottom: -1 }}>
                    {urlState.tickets.length}
                  </UI.Badge>
                )}
              </UI.Button>
              {urlState.tickets.length > 0 && (
                <UI.Button onClick={() => resetFilters()} sc={{ color: 'gray.500', ml: 2 }}>
                  <UI.Icon>
                    <XCircle />
                  </UI.Icon>
                  {' '}
                  {t('clear')}
                </UI.Button>
              )}
            </>
          )}
        </UI.FlexContainer>

        {eventId && (
          <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
            <UI.Tabs sc={{ border: true }}>
              <Tab pathname={`/${project.id}/dashboard/${eventId}`}>
                {t('registrations')}
              </Tab>
              <Tab pathname={`/${project.id}/dashboard/${eventId}/demographics`}>
                {t('demographics_and_origin')}
              </Tab>
              <Tab pathname={`/${project.id}/dashboard/${eventId}/fundraising`}>
                {t('fundraising')}
              </Tab>
            </UI.Tabs>
          </div>
        )}

        {children}
      </UI.GridContainer>
    </UI.Section>
  );
};

const Tab = ({ pathname, children }: { pathname: string, children: ReactNode }) => (
  <UI.Tab to={{ pathname, search: window.location.search }} exact>
    {children}
  </UI.Tab>
);

export default DashboardLayout;
